// AddDeviceModal.js
// Modal to add a new monitored device for ADB time monitoring

import React, { useState } from 'react';

export default function AddDeviceModal({ routerName, onClose, onDeviceAdded }) {
  const [deviceIp, setDeviceIp] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();

    // Validate IP address format
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipRegex.test(deviceIp)) {
      setError('Invalid IP address format');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch('/api/monitored-devices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          deviceIp: deviceIp.trim(),
          deviceName: deviceName.trim() || deviceIp,
          routerName: routerName
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add device');
      }

      const data = await response.json();
      console.log('[AddDeviceModal] Device added:', data);

      // Notify parent and close
      if (onDeviceAdded) {
        onDeviceAdded(data.device);
      }
      onClose();
    } catch (err) {
      console.error('[AddDeviceModal] Error adding device:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem'
    },
    modal: {
      backgroundColor: '#0f1a29',
      borderRadius: '12px',
      border: '1px solid #23314a',
      maxWidth: '500px',
      width: '100%',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
    },
    header: {
      padding: '1.5rem',
      borderBottom: '1px solid #23314a',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#e2e8f0'
    },
    closeButton: {
      background: 'transparent',
      border: 'none',
      color: '#94a3b8',
      fontSize: '1.5rem',
      cursor: 'pointer',
      padding: '0.25rem',
      lineHeight: 1,
      transition: 'color 0.2s'
    },
    body: {
      padding: '1.5rem'
    },
    label: {
      display: 'block',
      color: '#cbd5e1',
      fontSize: '0.875rem',
      fontWeight: '500',
      marginBottom: '0.5rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      backgroundColor: '#0c1422',
      border: '1px solid #2a3a56',
      borderRadius: '6px',
      color: '#e2e8f0',
      fontSize: '0.875rem',
      outline: 'none',
      transition: 'border-color 0.2s'
    },
    inputFocus: {
      borderColor: '#4a7dff'
    },
    error: {
      marginTop: '1rem',
      padding: '0.75rem',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '6px',
      color: '#fca5a5',
      fontSize: '0.875rem'
    },
    footer: {
      padding: '1rem 1.5rem',
      borderTop: '1px solid #23314a',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem'
    },
    button: {
      padding: '0.625rem 1.25rem',
      borderRadius: '6px',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s'
    },
    cancelButton: {
      backgroundColor: '#1e293b',
      color: '#cbd5e1'
    },
    submitButton: {
      backgroundColor: '#4a7dff',
      color: '#ffffff'
    },
    submitButtonDisabled: {
      backgroundColor: '#2a3a56',
      color: '#64748b',
      cursor: 'not-allowed'
    },
    helper: {
      marginTop: '0.5rem',
      color: '#64748b',
      fontSize: '0.75rem'
    },
    routerInfo: {
      marginBottom: '1.5rem',
      padding: '0.75rem',
      backgroundColor: '#132038',
      borderRadius: '6px',
      color: '#94a3b8',
      fontSize: '0.875rem'
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Add Monitored Device</h2>
          <button
            style={styles.closeButton}
            onClick={onClose}
            onMouseOver={(e) => e.target.style.color = '#e2e8f0'}
            onMouseOut={(e) => e.target.style.color = '#94a3b8'}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.body}>
            <div style={styles.routerInfo}>
              <strong>Router:</strong> {routerName}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>
                Device IP Address <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                style={styles.input}
                value={deviceIp}
                onChange={(e) => setDeviceIp(e.target.value)}
                onFocus={(e) => e.target.style.borderColor = '#4a7dff'}
                onBlur={(e) => e.target.style.borderColor = '#2a3a56'}
                placeholder="e.g., 10.5.1.91"
                required
              />
              <div style={styles.helper}>
                Enter the IP address of the Android device connected to this router
              </div>
            </div>

            <div>
              <label style={styles.label}>
                Device Name (optional)
              </label>
              <input
                type="text"
                style={styles.input}
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                onFocus={(e) => e.target.style.borderColor = '#4a7dff'}
                onBlur={(e) => e.target.style.borderColor = '#2a3a56'}
                placeholder="e.g., Store Display 1"
              />
              <div style={styles.helper}>
                Optional friendly name for this device
              </div>
            </div>

            {error && (
              <div style={styles.error}>
                {error}
              </div>
            )}
          </div>

          <div style={styles.footer}>
            <button
              type="button"
              style={{ ...styles.button, ...styles.cancelButton }}
              onClick={onClose}
              onMouseOver={(e) => e.target.style.backgroundColor = '#334155'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#1e293b'}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.button,
                ...(saving || !deviceIp ? styles.submitButtonDisabled : styles.submitButton)
              }}
              disabled={saving || !deviceIp}
              onMouseOver={(e) => {
                if (!saving && deviceIp) e.target.style.backgroundColor = '#5a8dff';
              }}
              onMouseOut={(e) => {
                if (!saving && deviceIp) e.target.style.backgroundColor = '#4a7dff';
              }}
            >
              {saving ? 'Adding...' : 'Add Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
